/**
 * Krtona PWA install toast.
 *
 * Android / desktop Chromium: catches `beforeinstallprompt`, shows a branded
 * toast whose CTA triggers the REAL native install dialog.
 *
 * iOS Safari: Apple allows no programmatic prompt, so eligible Safari users
 * get a one-time instructional toast (Share → Add to Home Screen).
 *
 * Professional behavior rules:
 *  - never shown when already installed / running standalone
 *  - never shown inside in-app browsers (Instagram, Facebook, WhatsApp…)
 *  - shown once per visit after a short delay, snoozed 14 days on dismiss,
 *    permanently hidden once installed
 *  - fully RTL, keyboard-accessible, 44px+ touch targets
 *
 * Debug: append ?pwa-debug=android or ?pwa-debug=ios to force the toast.
 */
(function () {
  'use strict';

  var SNOOZE_KEY = 'krtona-pwa-snooze-until';
  var INSTALLED_KEY = 'krtona-pwa-installed';
  var SNOOZE_DAYS = 14;
  var SHOW_DELAY_MS = 4000;

  var debugMode = null;
  try {
    debugMode = new URLSearchParams(location.search).get('pwa-debug');
  } catch (e) {
    /* noop */
  }

  function isStandalone() {
    return (
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true
    );
  }

  function isInAppBrowser() {
    var ua = navigator.userAgent || '';
    return /FBAN|FBAV|Instagram|WhatsApp|Snapchat|TikTok|Twitter|Line\//i.test(ua);
  }

  function isIosSafari() {
    var ua = navigator.userAgent || '';
    var isIos = /iPhone|iPad|iPod/i.test(ua) || (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1);
    var isSafari = /Safari/i.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS|GSA/i.test(ua);
    return isIos && isSafari;
  }

  function isSnoozed() {
    try {
      if (localStorage.getItem(INSTALLED_KEY)) return true;
      var until = parseInt(localStorage.getItem(SNOOZE_KEY) || '0', 10);
      return Date.now() < until;
    } catch (e) {
      return false;
    }
  }

  function snooze() {
    try {
      localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_DAYS * 864e5));
    } catch (e) {
      /* noop */
    }
  }

  function markInstalled() {
    try {
      localStorage.setItem(INSTALLED_KEY, '1');
    } catch (e) {
      /* noop */
    }
  }

  var ICON_APP =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="5" y="2.5" width="14" height="19" rx="3"/><path d="M12 6.5v7m0 0-2.8-2.8M12 13.5l2.8-2.8"/><line x1="9" y1="17.5" x2="15" y2="17.5"/></svg>';
  var ICON_SHARE =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 3v11"/><path d="m8.5 6 3.5-3.5L15.5 6"/><path d="M6.5 10H5.5a1.8 1.8 0 0 0-1.8 1.8v7.4A1.8 1.8 0 0 0 5.5 21h13a1.8 1.8 0 0 0 1.8-1.8v-7.4a1.8 1.8 0 0 0-1.8-1.8h-1"/></svg>';

  function buildToast(mode, onInstall, onDismiss) {
    var toast = document.createElement('div');
    toast.className = 'krtona-pwa-toast';
    toast.setAttribute('role', 'dialog');
    toast.setAttribute('aria-label', 'تثبيت تطبيق كرتونة');
    toast.dir = 'rtl';

    var body =
      mode === 'ios'
        ? '<p class="krtona-pwa-toast__title">أضِف كرتونة لشاشتك الرئيسية</p>' +
          '<p class="krtona-pwa-toast__sub">اضغط زر المشاركة ' +
          '<span class="krtona-pwa-toast__inline-icon">' + ICON_SHARE + '</span>' +
          ' ثم اختر «إضافة إلى الشاشة الرئيسية»</p>'
        : '<p class="krtona-pwa-toast__title">ثبّت تطبيق كرتونة</p>' +
          '<p class="krtona-pwa-toast__sub">تسوق أسرع من شاشتك الرئيسية — بدون متصفح</p>';

    toast.innerHTML =
      '<button type="button" class="krtona-pwa-toast__close" data-pwa-dismiss aria-label="إغلاق">' +
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>' +
      '</button>' +
      '<span class="krtona-pwa-toast__badge">' + ICON_APP + '</span>' +
      '<div class="krtona-pwa-toast__content">' + body + '</div>' +
      (mode === 'ios'
        ? ''
        : '<div class="krtona-pwa-toast__actions">' +
          '<button type="button" class="krtona-pwa-toast__btn" data-pwa-install>تثبيت</button>' +
          '</div>');

    function close() {
      toast.classList.remove('is-visible');
      window.setTimeout(function () {
        toast.remove();
      }, 250);
    }

    var installBtn = toast.querySelector('[data-pwa-install]');
    if (installBtn) {
      installBtn.addEventListener('click', function () {
        close();
        onInstall && onInstall();
      });
    }
    toast.querySelector('[data-pwa-dismiss]').addEventListener('click', function () {
      close();
      onDismiss && onDismiss();
    });

    document.body.appendChild(toast);
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () {
        toast.classList.add('is-visible');
      });
    });
    return toast;
  }

  function scheduleToast(mode, onInstall) {
    window.setTimeout(function () {
      if (document.querySelector('.krtona-pwa-toast')) return;
      buildToast(mode, onInstall, snooze);
    }, SHOW_DELAY_MS);
  }

  if (isStandalone()) {
    markInstalled();
    return;
  }
  if (!debugMode && (isInAppBrowser() || isSnoozed())) return;

  // --- Android / desktop Chromium: real install prompt ---
  var deferredPrompt = null;
  window.addEventListener('beforeinstallprompt', function (event) {
    event.preventDefault();
    deferredPrompt = event;
    if (isSnoozed() && !debugMode) return;
    scheduleToast('android', function () {
      if (!deferredPrompt) return;
      deferredPrompt.prompt();
      deferredPrompt.userChoice.then(function (choice) {
        if (choice && choice.outcome === 'accepted') markInstalled();
        else snooze();
        deferredPrompt = null;
      });
    });
  });

  window.addEventListener('appinstalled', function () {
    markInstalled();
    var open = document.querySelector('.krtona-pwa-toast');
    if (open) open.remove();
  });

  // --- iOS Safari: instructional toast (no API exists) ---
  if (isIosSafari() && !debugMode) {
    scheduleToast('ios', null);
  }

  // --- Debug forcing ---
  if (debugMode === 'android') scheduleToast('android', function () {});
  if (debugMode === 'ios') scheduleToast('ios', null);
})();
